import { useState } from 'react'

import { ApiError } from '../../api/client'
import type { Trip } from '../../api/types'
import { useSettings, useUpdateTrip } from '../../hooks/queries'
import { Blasenwahl, type Blasensatz } from '../settings/Blasenwahl'
import { useTexte } from '../../texte'

/**
 * Die Farben der Sprechblasen **dieser Reise** (#49).
 *
 * Vorher galten sie übergreifend: wer zwei Berichte nebeneinander führte, sah
 * beide im selben Ton. Jetzt steht die Farbe an der Reise — und die
 * Einstellung in der Verwaltung ist die **Voreinstellung**, die beim Anlegen
 * einmal kopiert wird.
 *
 * **Kopiert und nicht nachgeschlagen**, und deshalb steht hier der Knopf
 * *Voreinstellung übernehmen*: eine bestehende Reise ändert sich nicht mehr
 * von selbst, wenn jemand die Voreinstellung verstellt, aber nachziehen soll
 * sie sich lassen, ohne dass man die Farbe abschreibt.
 *
 * **Nur der Reisende**, wie beim Hintergrund — der Bericht ist seiner, und die
 * Farbe gilt für alle, die mitlesen. Der Server prüft dasselbe noch einmal.
 */
export function Blasenfarben({ trip }: { trip: Trip }) {
  const texte = useTexte()
  const t = texte.reise.blasen
  const speichern = useUpdateTrip(trip.id)
  const settings = useSettings()
  const [gemeldet, setGemeldet] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const blasen = trip.chat_bubbles ?? { own: null, other: null }
  // Die Voreinstellung kommt aus den allgemeinen Einstellungen -- dieselbe
  // Quelle, aus der auch der Server beim Anlegen kopiert. Fehlt sie (kein Netz
  // beim ersten Aufruf), steht der Knopf nicht da, statt eine Farbe zu
  // versprechen, die niemand kennt.
  const vorwerte = settings.data?.settings
  const voreinstellung: Blasensatz | undefined =
    vorwerte === undefined
      ? undefined
      : {
          chat_own_color: vorwerte.chat_own_color ?? '',
          chat_other_color: vorwerte.chat_other_color ?? '',
        }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.titel}</h2>
      <Blasenwahl
        lead={t.leadReise}
        werte={{
          chat_own_color: blasen.own ?? '',
          chat_other_color: blasen.other ?? '',
        }}
        laeuft={speichern.isPending}
        fehler={problem}
        gemeldet={gemeldet}
        {...(voreinstellung !== undefined ? { voreinstellung } : {})}
        onSpeichern={async (satz: Blasensatz) => {
          setGemeldet(false)
          setProblem(null)
          try {
            await speichern.mutateAsync(satz)
            setGemeldet(true)
          } catch (fehler) {
            setProblem(
              fehler instanceof ApiError && fehler.offline
                ? texte.allgemein.ohneNetz
                : fehler instanceof Error
                  ? fehler.message
                  : texte.allgemein.schiefgelaufen,
            )
            throw fehler
          }
        }}
      />
    </div>
  )
}
